<script setup lang="ts">
import { useFormatters } from '@/composables/useFormatters'

const { formatCurrency, formatPercent, formatNumber } = useFormatters()

export interface ResultRow {
  label: string
  value: string | number
  format?: 'currency' | 'percent' | 'number' | 'plain'
}

defineProps<{
  rows: ResultRow[]
  caption?: string
}>()

function format(row: ResultRow): string {
  if (typeof row.value === 'string') return row.value
  switch (row.format) {
    case 'currency':
      return formatCurrency(row.value)
    case 'percent':
      return formatPercent(row.value)
    case 'number':
      return formatNumber(row.value, 2)
    default:
      return String(row.value)
  }
}
</script>

<template>
  <table class="result-table">
    <caption v-if="caption" class="sr-only">
      {{
        caption
      }}
    </caption>
    <tbody>
      <tr v-for="row in rows" :key="row.label">
        <th scope="row" class="result-table__label">{{ row.label }}</th>
        <td class="result-table__value">{{ format(row) }}</td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
.result-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-base);
}

.result-table tr:not(:last-child) {
  border-bottom: 1px solid var(--color-border);
}

.result-table th,
.result-table td {
  padding: var(--space-3) 0;
  text-align: left;
  vertical-align: top;
}

.result-table__label {
  font-weight: var(--font-medium);
  color: var(--color-text-muted);
  width: 60%;
}

.result-table__value {
  font-weight: var(--font-semibold);
  color: var(--color-text);
  text-align: right;
  font-variant-numeric: tabular-nums;
}
</style>
